// src/pages/Debater.js
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/authContext';
import Button from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { Input, Textarea } from '../components/ui/Input';
import { Spinner } from '../components/ui/Spinner';

const API_BASE = process.env.REACT_APP_API_URL;

export default function Debater() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [motion, setMotion] = useState('');
  const [side, setSide] = useState('Opposition'); // student's side
  const [chat, setChat] = useState([]); // [{role:'user'|'assistant', content}]
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [starting, setStarting] = useState(false);
  const [hasStarted, setHasStarted] = useState(false);
  const [error, setError] = useState('');
  const [tipsOpen, setTipsOpen] = useState(true);

  const scrollRef = useRef(null);

  useEffect(() => { if (!user) navigate('/login'); }, [user, navigate]);

  // Auto-scroll chat
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [chat, loading]);

  // Optional: restore history on load
  useEffect(() => {
    const restore = async () => {
      try {
        const r = await fetch(`${API_BASE}/api/debate/history?userId=${encodeURIComponent(user?.id || 'guest')}`);
        const d = await r.json();
        if (d.motion) setMotion(d.motion);
        if (d.side) setSide(d.side);
        if (Array.isArray(d.messages)) setChat(d.messages);
        if (d.motion && d.messages?.length) setHasStarted(true);
      } catch { /* ignore */ }
    };
    if (user) restore();
  }, [user]);

  const generateMotion = async () => {
    setError('');
    try {
      const r = await fetch(`${API_BASE}/api/generate-motion`);
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || 'Failed to generate motion');
      setMotion(d.motion);
    } catch (e) {
      setError('Could not generate a motion.');
    }
  };

  const startDebate = async () => {
    setError('');
    setChat([]);
    if (!motion.trim()) {
      setError('Enter or generate a debate motion first.');
      return;
    }
    setStarting(true);
    try {
      const r = await fetch(`${API_BASE}/api/debate/start`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId: user?.id || 'guest',
          motion: motion.trim(),
          side,
        }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || 'Failed to start debate');
      if (d.opening) setChat([{ role: 'assistant', content: d.opening }]);
      setHasStarted(true);
    } catch (e) {
      setError(e.message || 'Failed to start debate');
    } finally {
      setStarting(false);
    }
  };

  const ensureStarted = async () => {
    if (hasStarted) return true;
    await startDebate();
    return true;
  };

  const sendMessage = async () => {
    setError('');
    const msg = input.trim();
    if (!msg) return;

    const ok = await ensureStarted();
    if (!ok) return;

    setChat(prev => [...prev, { role: 'user', content: msg }]);
    setInput('');
    setLoading(true);
    try {
      const r = await fetch(`${API_BASE}/api/debate/message`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId: user?.id || 'guest',
          content: msg,
        }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || 'Failed to get reply');
      setChat(prev => [...prev, { role: 'assistant', content: d.reply }]);
    } catch (e) {
      setChat(prev => [...prev, { role: 'assistant', content: 'Error generating response.' }]);
    } finally {
      setLoading(false);
    }
  };

  const resetDebate = async () => {
    setError('');
    setChat([]);
    setInput('');
    setHasStarted(false);
    try {
      const r = await fetch(`${API_BASE}/api/debate/reset`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId: user?.id || 'guest' }),
      });
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        throw new Error(d.error || 'Failed to reset');
      }
    } catch (e) {
      setError(e.message || 'Failed to reset debate');
    }
  };

  // Quick inserts
  const quickInsert = (text) => setInput(i => (i ? `${i}\n${text}` : text));

  if (!user) return null;

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">AI Debater</h1>
          <p className="text-sm text-gray-600">
            Argue on your chosen side. The AI will take the opposite and remember the round.
          </p>
        </div>
        <div className="flex gap-2">
          <Button type="button" variant="secondary" onClick={() => navigate('/training')}>Training</Button>
        </div>
      </div>

      {/* Setup */}
      <Card title="Setup" subtitle="Enter or generate a motion and select your side">
        <div className="grid sm:grid-cols-3 gap-3">
          <div className="sm:col-span-2">
            <label className="block text-xs font-medium text-gray-600 mb-1">Motion</label>
            <Input
              placeholder='e.g., This House would ban targeted political advertising online'
              value={motion}
              onChange={(e) => setMotion(e.target.value)}
            />
          </div>
          <div className="flex items-end gap-2">
            <Button type="button" variant="secondary" onClick={generateMotion}>Generate</Button>
            <Button type="button" onClick={startDebate} disabled={starting || !motion.trim()}>
              {starting ? <span className="inline-flex items-center gap-2"><Spinner />Starting…</span> : 'Start'}
            </Button>
          </div>
        </div>

        <div className="mt-3 grid sm:grid-cols-3 gap-3">
          <div className="sm:col-span-1">
            <label className="block text-xs font-medium text-gray-600 mb-1">Your Side</label>
            <select
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
              value={side}
              onChange={(e) => setSide(e.target.value)}
              disabled={hasStarted}
              title={hasStarted ? 'Side is locked for this round' : 'Choose your side'}
            >
              <option>Government</option>
              <option>Opposition</option>
            </select>
            {hasStarted && <p className="mt-1 text-xs text-gray-500">Side locked. Start a new debate to change.</p>}
          </div>
          <div className="sm:col-span-2">
            {error && (
              <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                {error}
              </div>
            )}
          </div>
        </div>
      </Card>

      {/* Tips */}
      <Card
        title="Roundcraft Tips"
        subtitle="Use these to keep your speeches sharp"
        actions={
          <button
            type="button"
            onClick={() => setTipsOpen(o => !o)}
            className="text-sm text-gray-600 hover:text-gray-800"
          >
            {tipsOpen ? 'Hide' : 'Show'}
          </button>
        }
      >
        {tipsOpen && (
          <div className="text-sm text-gray-700 space-y-2">
            <p><strong>Signpost:</strong> Frame, two contentions, weighing.</p>
            <p><strong>Clash first:</strong> Address their biggest impact or mechanism before extending.</p>
            <p><strong>Weigh:</strong> Magnitude, probability, reversibility, timeframe, scope.</p>
            <div className="flex flex-wrap gap-2 pt-1">
              <Button type="button" variant="secondary" onClick={() => quickInsert('Framing: This debate is about ...')}>
                Insert Framing
              </Button>
              <Button type="button" variant="secondary" onClick={() => quickInsert('Clash: The central question is whether ...')}>
                Insert Clash
              </Button>
              <Button type="button" variant="secondary" onClick={() => quickInsert('Weighing: Even if their claim is true, ours matters more because ...')}>
                Insert Weighing
              </Button>
              <Button type="button" variant="secondary" onClick={() => quickInsert('POI: Can you explain the causal link between ... and ...?')}>
                Insert POI
              </Button>
            </div>
          </div>
        )}
      </Card>

      {/* Debate */}
      <Card title="Debate" subtitle="Make your case. The AI responds as the opposite bench.">
        <div
          ref={scrollRef}
          className="h-96 overflow-y-auto border border-gray-200 rounded-md bg-white p-4 space-y-4"
        >
          {chat.map((m, i) => (
            <div
              key={i}
              className={`px-4 py-2 rounded-lg shadow-sm w-fit max-w-[80%] whitespace-pre-wrap ${
                m.role === 'user'
                  ? 'bg-blue-50 text-blue-800 ml-auto'
                  : 'bg-gray-100 text-gray-800 mr-auto'
              }`}
            >
              {m.content}
            </div>
          ))}
          {loading && (
            <div className="text-gray-500 text-sm italic">AI is thinking…</div>
          )}
        </div>

        <div className="mt-4 grid sm:grid-cols-6 gap-3">
          <div className="sm:col-span-5">
            <Textarea
              rows={3}
              placeholder="Write your speech, rebuttal, extension, or POI…"
              value={input}
              onChange={(e) => setInput(e.target.value)}
            />
          </div>
          <div className="sm:col-span-1 flex flex-col gap-2">
            <Button type="button" onClick={sendMessage} disabled={loading || !input.trim()}>
              {loading ? 'Sending…' : (chat.length ? 'Respond' : 'Begin')}
            </Button>
            <Button type="button" variant="ghost" onClick={resetDebate}>New Debate</Button>
          </div>
        </div>
      </Card>
    </div>
  );
}
